import { Box, Container, Stack, Typography, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

function GradientText({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        background: "linear-gradient(135deg, #3d7aff 0%, #c9b107 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      }}
    >
      {children}
    </Box>
  );
}

const rotatingWords = [
  "SA Labour Law",
  "POPIA",
  "BCEA",
  "LRA",
  "EE Act",
  "OHS Act",
];

function RotatingWord() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      component="span"
      sx={{
        background: "linear-gradient(135deg, #c9b107, #3d7aff)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        fontWeight: 600,
        display: "inline-block",
        minWidth: 120,
      }}
    >
      {rotatingWords[index]}
    </Box>
  );
}

export function HeroSection() {
  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Space background image */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "url(https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.3,
        }}
      />

      {/* Nebula gradient overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.8,
          background: `
            radial-gradient(ellipse 80% 50% at 20% 40%, rgba(26,76,216,0.6) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 80% 20%, rgba(61,122,255,0.4) 0%, transparent 50%),
            radial-gradient(ellipse 50% 50% at 50% 80%, rgba(201,177,7,0.2) 0%, transparent 50%)
          `,
          animation: "aurora 8s ease-in-out infinite alternate",
          "@keyframes aurora": {
            "0%": { transform: "scale(1) rotate(0deg)" },
            "100%": { transform: "scale(1.05) rotate(1deg)" },
          },
        }}
      />

      {/* Gradient fade to dark */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(5,5,16,0.0) 0%, rgba(5,5,16,0.5) 50%, rgba(5,5,16,1) 100%)",
        }}
      />

      {/* Stars overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        {[...Array(30)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: i % 3 === 0 ? 2 : 1,
              height: i % 3 === 0 ? 2 : 1,
              borderRadius: "50%",
              backgroundColor: i % 5 === 0 ? "#c9b107" : "#000022",
              left: `${(i * 3.3) % 100}%`,
              top: `${(i * 7.1) % 100}%`,
              opacity: 0.2 + (i % 4) * 0.15,
              animation: `starTwinkle ${2 + (i % 3)}s ease-in-out infinite alternate`,
            }}
          />
        ))}
        <style>{`
          @keyframes starTwinkle { 0% { opacity: 0.15; } 100% { opacity: 0.25; } }
        `}</style>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, pt: 12 }}>
        <Stack spacing={4} sx={{ maxWidth: 800 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "40px", sm: "56px", md: "72px" },
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: "#f0f0f5",
            }}
          >
            Modern HR Management
            <br />
            for South African
            <br />
            <GradientText>Employers</GradientText>
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "#a0a5b5",
              maxWidth: 600,
              lineHeight: 1.6,
              fontWeight: 400,
              fontSize: { xs: 16, md: 20 },
            }}
          >
            Streamline onboarding, skills development, compliance, and
            industrial relations — all in one platform built for{" "}
            <RotatingWord />
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              size="large"
              sx={{
                background: "linear-gradient(135deg, #1a4cd8, #3d7aff)",
                px: 4,
                py: 1.5,
                borderRadius: "9999px",
                textTransform: "none",
                fontSize: 16,
                fontWeight: 600,
                "&:hover": {
                  background: "linear-gradient(135deg, #3d7aff, #1a4cd8)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s",
              }}
            >
              Get Started Free →
            </Button>
            <Button
              component={Link}
              to="/about"
              variant="outlined"
              size="large"
              sx={{
                borderColor: "rgba(255,255,255,0.15)",
                color: "#a0a5b5",
                px: 4,
                py: 1.5,
                borderRadius: "9999px",
                textTransform: "none",
                fontSize: 16,
                fontWeight: 500,
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "#f0f0f5",
                },
              }}
            >
              Learn More
            </Button>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mt: 1 }}
          >
            {[
              "Free for 1-10 employees",
              "No credit card required",
              "Setup in 5 minutes",
            ].map((text) => (
              <Stack
                key={text}
                direction="row"
                spacing={0.5}
                alignItems="center"
              >
                <Box sx={{ color: "#22c55e", fontSize: 14, fontWeight: 700 }}>
                  ✓
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#a0a5b5", fontSize: 14 }}
                >
                  {text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
